import { motion, AnimatePresence } from 'framer-motion';
import useFetchCandidateProfile from '../api/useFetchCandidateProfile';
import { useEffect, useState } from 'react';
import CandidatesProfileDrawerTab from './CandidatesProfileDrawerTab';
import { DRAWER_TAB_ITEMS, type TimelineFilter } from '../constants';
import CandidatesProfileCard from './CandidatesProfileCard';
import CandidatesMatchingCardDrawer from './CandidatesMatchingCardDrawer';
import CandidatesTimelineCard from './CandidatesTimelineCard';
import clsx from 'clsx';
import type { AgentApiCandidateBoardsIndex } from '@/types/serializers';
import { Button } from '@/components/Button';
import { useAtomValue } from 'jotai';
import { isCandidateProfileEditAtom } from '../atom/candidatesAtom';
import { Link } from '@tanstack/react-router';

type Props = {
  isOpen: boolean;
  profileId: number | null;
  onClose: () => void;
  onBoardChanged?: () => void;
  boards?: AgentApiCandidateBoardsIndex;
};

export default function CandidatesShowDrawer({
  isOpen,
  onClose,
  profileId,
  onBoardChanged,
  boards = undefined,
}: Props) {
  const isCandidateProfileEdit = useAtomValue(isCandidateProfileEditAtom);
  const params = new URLSearchParams(window.location.search);
  const tabParam = params.get('tab');
  const { data: candidateProfile, refetch: candidateProfileRefetch } =
    useFetchCandidateProfile(profileId);
  const getTabFromUrl = (): (typeof DRAWER_TAB_ITEMS)[number]['value'] => {
    if (typeof window === 'undefined') return 'profile';

    return tabParam && DRAWER_TAB_ITEMS.some((item) => item.value === tabParam)
      ? (tabParam as (typeof DRAWER_TAB_ITEMS)[number]['value'])
      : 'profile';
  };
  const [tab, setTab] = useState<(typeof DRAWER_TAB_ITEMS)[number]['value']>(getTabFromUrl());
  const [timelineFilter, setTimelineFilter] = useState<TimelineFilter>('all');

  const handleTabChange = (newTab: (typeof DRAWER_TAB_ITEMS)[number]['value']) => {
    setTab(newTab);

    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', newTab);
      window.history.replaceState({}, '', url.toString());
    }
  };

  useEffect(() => {
    const handlePopState = () => {
      setTab(getTabFromUrl());
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('popstate', handlePopState);
      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, []);

  return (
    <AnimatePresence>
      {isOpen && candidateProfile && (
        <>
          <motion.div
            className="fixed inset-y-0 right-0 left-[250px] z-110 bg-black/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="fixed top-0 right-0 left-[250px] z-120 flex h-[calc(100vh-70px)] flex-col bg-white shadow-[0px_0px_12px_0px_#00000033]"
            initial={{ y: '100%' }}
            animate={{ y: '70px' }}
            exit={{ y: '100%' }}
            transition={{ type: 'tween', duration: 0.3, ease: 'easeOut' }}
          >
            <div className="sticky top-0 z-10 bg-white">
              <CandidatesProfileDrawerTab
                candidateId={Number(profileId)}
                tab={tab}
                setTab={handleTabChange}
                candidateName={candidateProfile?.name || ''}
                organizationUserIds={candidateProfile?.organization_users?.map((u) => u.id) ?? []}
                files={candidateProfile?.document_files ?? []}
                companyName={candidateProfile?.company_name ?? ''}
                jobPosition={candidateProfile?.job_position ?? ''}
                age={candidateProfile?.age ? String(candidateProfile?.age) : ''}
                scores={{
                  suggest: candidateProfile?.counter_cache.count_of_suggest || 0,
                  documentScreening:
                    candidateProfile?.counter_cache.count_of_document_screening || 0,
                  interview: candidateProfile?.counter_cache.count_of_interview || 0,
                  offer: candidateProfile?.counter_cache.count_of_offer || 0,
                  close: candidateProfile?.counter_cache.count_of_close || 0,
                }}
                candidateProfileRefetch={candidateProfileRefetch}
                candidateBoard={{
                  id: candidateProfile?.candidate_board?.id ?? undefined,
                  title: candidateProfile?.candidate_board?.title ?? undefined,
                  colorType: candidateProfile?.candidate_board?.color_type ?? undefined,
                }}
                cacandidateBoards={boards?.records ?? []}
                drawerClose={onClose}
                onBoardChanged={onBoardChanged}
              />
            </div>
            <div
              data-candidates-matching-section
              className="bg-background/gray relative flex flex-1 overflow-y-auto"
            >
              {/* pb-24 は右下の固定ボタンに最下部が隠れないため */}
              <section
                className={clsx(
                  'hidden-scrollbar w-full flex-1 overflow-auto pb-24',
                  tab !== 'matching' && 'px-8 pt-6'
                )}
              >
                {tab === 'profile' && (
                  <CandidatesProfileCard
                    candidateId={Number(profileId)}
                    candidateName={candidateProfile?.name ?? ''}
                    organizationUsers={candidateProfile?.organization_users ?? []}
                    customData={candidateProfile?.custom_data ?? []}
                    candidateProfileRefetch={candidateProfileRefetch}
                    avatarUrl={candidateProfile?.avatar?.url ?? ''}
                  />
                )}
                {tab === 'timeline' && (
                  <CandidatesTimelineCard
                    candidateId={Number(profileId)}
                    filter={timelineFilter}
                    onFilterChange={setTimelineFilter}
                  />
                )}
                {tab === 'matching' && (
                  <CandidatesMatchingCardDrawer candidateId={String(profileId)} />
                )}
              </section>
              {!isCandidateProfileEdit && (
                <Button className="fixed right-6 bottom-6 h-[52px]" asChild>
                  <Link
                    to="/agent/candidates/$candidateProfileId"
                    params={{ candidateProfileId: profileId?.toString() || '' }}
                  >
                    求職者ページを開く
                  </Link>
                </Button>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
